import { useTranslation } from 'react-i18next';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

/**
 * Notifications.
 *
 * A toast reports WHAT ACTUALLY HAPPENED (CLAUDE.md section 8). It is raised
 * after a request resolves, never optimistically when a button is pressed, so
 * "Saved" on screen always means the server saved it.
 *
 * Accessibility: the region is a live region, so a screen reader announces the
 * message without the focus moving. `assertive` for failures, which interrupt;
 * `polite` for everything else, which waits for a pause.
 *
 * Text is passed in already translated — the caller holds the `t` function and
 * the context that decides which key applies.
 */

export type ToastTone = 'success' | 'danger' | 'warning' | 'info';

export interface Toast {
  readonly id: number;
  readonly tone: ToastTone;
  readonly message: string;
  readonly description?: string;
}

interface ToastContextValue {
  readonly toasts: readonly Toast[];
  show(toast: Omit<Toast, 'id'>): void;
  dismiss(id: number): void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

/** How long a toast stays before it withdraws itself, in milliseconds. */
const DISMISS_AFTER = 6000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<readonly Toast[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const show = useCallback(
    (toast: Omit<Toast, 'id'>) => {
      const id = nextId.current++;
      setToasts((current) => [...current, { ...toast, id }]);
      // A failure stays until it is dismissed. Something went wrong and the
      // user may need to read it twice; auto-hiding it is how a real error
      // goes unnoticed.
      if (toast.tone !== 'danger') {
        setTimeout(() => dismiss(id), DISMISS_AFTER);
      }
    },
    [dismiss],
  );

  const value = useMemo(
    () => ({ toasts, show, dismiss }),
    [toasts, show, dismiss],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used inside a ToastProvider.');
  }
  return context;
}

const TONE_CLASSES: Record<ToastTone, string> = {
  success: 'border-success-200 bg-success-50 text-success-800',
  danger: 'border-danger-200 bg-danger-50 text-danger-800',
  warning: 'border-warning-200 bg-warning-50 text-warning-800',
  info: 'border-secondary-200 bg-secondary-50 text-secondary-800',
};

function ToastViewport() {
  const { toasts, dismiss } = useToast();
  const { t } = useTranslation();

  return (
    <>
      {/*
        Two regions, because a live region's politeness is fixed at mount.
        `inset-inline-end` is a LOGICAL property: the stack sits at the trailing
        edge, which is the right in English and the left in Arabic.
      */}
      {(['polite', 'assertive'] as const).map((politeness) => (
        <div
          key={politeness}
          role={politeness === 'assertive' ? 'alert' : 'status'}
          aria-live={politeness}
          aria-atomic="false"
          className="pointer-events-none fixed bottom-4 z-50 flex w-full max-w-sm flex-col gap-2 px-4 [inset-inline-end:0]"
        >
          {toasts
            .filter((toast) =>
              politeness === 'assertive'
                ? toast.tone === 'danger'
                : toast.tone !== 'danger',
            )
            .map((toast) => (
              <div
                key={toast.id}
                className={`pointer-events-auto rounded-medium border px-4 py-3 text-body shadow-medium ${TONE_CLASSES[toast.tone]}`}
              >
                <p className="font-medium">{toast.message}</p>
                {toast.description ? (
                  <p className="mt-1 text-caption opacity-80">
                    {toast.description}
                  </p>
                ) : null}
                <button
                  type="button"
                  onClick={() => dismiss(toast.id)}
                  className="mt-2 text-caption underline underline-offset-2"
                >
                  {t('common.dismiss')}
                </button>
              </div>
            ))}
        </div>
      ))}
    </>
  );
}
